import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, CheckSquare, Square, ArrowRight, ArrowLeft, Inbox } from 'react-feather';
import { toast } from 'react-toastify';

/**
 * Generic dual-pane assign / unassign transfer list used by every
 * Configuration tab (delivery partners, revenue-share methods, charges).
 *
 * `adapter` (memoize it in the parent so its identity is stable!):
 *   loadCatalog()   -> Promise<[{ id, label }]>   full master list
 *   loadAssigned()  -> Promise<[{ id, label }]>   rows already assigned here
 *   assign(items)   -> Promise                    items: [{ id, displayName?, label }]
 *   unassign(ids)   -> Promise<void>              ids: string[]
 *
 * UX / correctness notes:
 *  - Mutations are optimistic but ROLL BACK to the prior panes on API failure,
 *    so the UI never silently desyncs from the server (the old code moved the
 *    row and only showed a toast on error).
 *  - Each pane keeps its own selection set — selecting in one pane no longer
 *    leaks into the other.
 *  - `requireDisplayName` gates Add until every selected row has a name.
 */
const TransferList = ({
    adapter,
    availableTitle = 'Available',
    assignedTitle = 'Selected',
    searchPlaceholder = 'Search…',
    requireDisplayName = false,
    canManage = true,
}) => {
    const { t } = useTranslation();
    const [available, setAvailable] = useState([]);
    const [assigned, setAssigned] = useState([]);
    const [selAvail, setSelAvail] = useState([]);
    const [selAssigned, setSelAssigned] = useState([]);
    const [displayNames, setDisplayNames] = useState({});
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const [catalog, assignedRows] = await Promise.all([
                adapter.loadCatalog(),
                adapter.loadAssigned(),
            ]);
            const assignedIds = new Set(assignedRows.map((r) => r.id));
            setAssigned(assignedRows);
            setAvailable(catalog.filter((c) => !assignedIds.has(c.id)));
            setSelAvail([]);
            setSelAssigned([]);
            setDisplayNames({});
        } catch (err) {
            toast.error(t('Failed to load configuration data'));
        } finally {
            setLoading(false);
        }
    }, [adapter, t]);

    useEffect(() => { reload(); }, [reload]);

    const filteredAvailable = useMemo(() => {
        const q = search.trim().toLowerCase();
        return q ? available.filter((a) => a.label?.toLowerCase().includes(q)) : available;
    }, [available, search]);

    const toggle = (setSel) => (id) =>
        setSel((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    const toggleAvail = toggle(setSelAvail);
    const toggleAssigned = toggle(setSelAssigned);

    const allAvailSelected = filteredAvailable.length > 0 && filteredAvailable.every((a) => selAvail.includes(a.id));
    const allAssignedSelected = assigned.length > 0 && assigned.every((a) => selAssigned.includes(a.id));

    const selectAllAvail = () =>
        setSelAvail(allAvailSelected ? [] : filteredAvailable.map((a) => a.id));
    const selectAllAssigned = () =>
        setSelAssigned(allAssignedSelected ? [] : assigned.map((a) => a.id));

    const dnameMissing = requireDisplayName
        && available.filter((a) => selAvail.includes(a.id)).some((a) => !(displayNames[a.id] || '').trim());

    const doAdd = async () => {
        const items = available.filter((a) => selAvail.includes(a.id));
        if (!items.length || busy) return;
        if (requireDisplayName && dnameMissing) {
            toast.error(t('Enter a display name for each selected item'));
            return;
        }
        const payload = items.map((i) => ({
            id: i.id,
            displayName: (displayNames[i.id] || '').trim(),
            label: requireDisplayName ? (displayNames[i.id] || '').trim() : i.label,
        }));
        const prevAvailable = available;
        const prevAssigned = assigned;
        setAvailable((a) => a.filter((x) => !selAvail.includes(x.id)));
        setAssigned((a) => [...a, ...payload.map((p) => ({ id: p.id, label: p.label }))]);
        setSelAvail([]);
        setBusy(true);
        try {
            await adapter.assign(payload);
            setDisplayNames({});
            toast.success(payload.length === 1
                ? t('Added {{count}} item', { count: payload.length })
                : t('Added {{count}} items', { count: payload.length }));
        } catch (err) {
            setAvailable(prevAvailable);
            setAssigned(prevAssigned);
            toast.error(t('Could not save — changes reverted'));
        } finally {
            setBusy(false);
        }
    };

    const doRemove = async () => {
        const items = assigned.filter((a) => selAssigned.includes(a.id));
        if (!items.length || busy) return;
        const ids = items.map((i) => i.id);
        const prevAvailable = available;
        const prevAssigned = assigned;
        setAssigned((a) => a.filter((x) => !selAssigned.includes(x.id)));
        setAvailable((a) => [...a, ...items.map((i) => ({ id: i.id, label: i.label }))]);
        setSelAssigned([]);
        setBusy(true);
        try {
            await adapter.unassign(ids);
            toast.success(ids.length === 1
                ? t('Removed {{count}} item', { count: ids.length })
                : t('Removed {{count}} items', { count: ids.length }));
        } catch (err) {
            setAvailable(prevAvailable);
            setAssigned(prevAssigned);
            toast.error(t('Could not remove — changes reverted'));
        } finally {
            setBusy(false);
        }
    };

    if (loading) {
        return (
            <div className="cfg-pane-empty" style={{ minHeight: 240 }}>
                <span className="cfg-spinner" aria-hidden />
                <p>{t('Loading…')}</p>
            </div>
        );
    }

    const renderRow = (row, pane) => {
        const selected = (pane === 'avail' ? selAvail : selAssigned).includes(row.id);
        const onToggle = pane === 'avail' ? toggleAvail : toggleAssigned;
        const showDname = pane === 'avail' && requireDisplayName && selected;
        return (
            <div
                key={row.id}
                className={`cfg-row ${selected ? 'selected' : ''}`}
                onClick={() => canManage && onToggle(row.id)}
                role="checkbox"
                aria-checked={selected}
                tabIndex={canManage ? 0 : -1}
                onKeyDown={(e) => {
                    if (canManage && (e.key === ' ' || e.key === 'Enter')) {
                        e.preventDefault();
                        onToggle(row.id);
                    }
                }}
            >
                <span className="cfg-row-check" aria-hidden>
                    {selected ? <CheckSquare size={16} /> : <Square size={16} />}
                </span>
                <span className="cfg-row-label" title={row.label}>{row.label || <em>{t('(unnamed)')}</em>}</span>
                {showDname && (
                    <input
                        type="text"
                        className={`cfg-row-dname ${!(displayNames[row.id] || '').trim() ? 'invalid' : ''}`}
                        placeholder={t('Display name…')}
                        value={displayNames[row.id] || ''}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => setDisplayNames((p) => ({ ...p, [row.id]: e.target.value }))}
                        aria-label={t('Display name for {{label}}', { label: row.label })}
                    />
                )}
            </div>
        );
    };

    // NOTE: a plain render function, NOT a nested component. Rendering it as
    // <Pane/> would give React a new component identity on every keystroke,
    // remounting the subtree and stealing focus from the search input.
    const renderPane = ({ title, rows, count, selectAll, allSelected, pane, searchable, emptyText }) => (
        <div className="cfg-pane">
            <div className="cfg-pane-head">
                <button
                    type="button"
                    className="cfg-selectall"
                    onClick={selectAll}
                    disabled={!canManage || rows.length === 0}
                    aria-label={allSelected ? t('Deselect all') : t('Select all')}
                    title={allSelected ? t('Deselect all') : t('Select all')}
                >
                    {allSelected ? <CheckSquare size={16} /> : <Square size={16} />}
                </button>
                <h3>{t(title)}</h3>
                <span className="cfg-pane-count">{count}</span>
            </div>
            {searchable && (
                <div className="cfg-pane-search">
                    <Search size={14} />
                    <input
                        type="search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={t(searchPlaceholder)}
                        aria-label={t('Search available items')}
                    />
                </div>
            )}
            <div className="cfg-pane-body">
                {rows.length === 0 ? (
                    <div className="cfg-pane-empty">
                        <Inbox size={26} />
                        <p>{emptyText}</p>
                    </div>
                ) : (
                    rows.map((r) => renderRow(r, pane))
                )}
            </div>
            <div className="cfg-pane-foot">
                {pane === 'avail' ? (
                    <button
                        type="button"
                        className="v3-btn v3-btn-primary"
                        onClick={doAdd}
                        disabled={!canManage || busy || selAvail.length === 0 || dnameMissing}
                    >
                        {t('Add selected')}{selAvail.length ? ` (${selAvail.length})` : ''}
                    </button>
                ) : (
                    <button
                        type="button"
                        className="v3-btn v3-btn-ghost"
                        onClick={doRemove}
                        disabled={!canManage || busy || selAssigned.length === 0}
                    >
                        {t('Remove selected')}{selAssigned.length ? ` (${selAssigned.length})` : ''}
                    </button>
                )}
            </div>
        </div>
    );

    return (
        <div className="cfg-transfer">
            {renderPane({
                title: availableTitle,
                rows: filteredAvailable,
                count: `${filteredAvailable.length}${search ? ` / ${available.length}` : ''}`,
                selectAll: selectAllAvail,
                allSelected: allAvailSelected,
                pane: 'avail',
                searchable: true,
                emptyText: search ? t('No matches — clear the search.') : t('Nothing left to add.'),
            })}
            <div className="cfg-transfer-rail" aria-hidden>
                <ArrowRight size={18} color="var(--v3-text-muted)" />
                <ArrowLeft size={18} color="var(--v3-text-muted)" />
            </div>
            {renderPane({
                title: assignedTitle,
                rows: assigned,
                count: assigned.length,
                selectAll: selectAllAssigned,
                allSelected: allAssignedSelected,
                pane: 'assigned',
                emptyText: t('None assigned yet.'),
            })}
        </div>
    );
};

export default TransferList;
